import { AgileMacroplanogram, storeSubcategory } from '../types/AgileMacroplanogramTypes';
import { ParsedFixture, MacroData } from '../types/zoneMapType';

export const DELIMITER = '/';
/**
 * normalize FixtureType to table / Rack
 */
export function normalizeFixtureType(fixtureType: string): string {
  return fixtureType.toLowerCase();
}

/**
 * parse FixtureId to {rowNumber, fixtureType, columnNumber} e.g: ROW1.TABLE1 -> {1, Table, 1}
 */
export function parseFixtureId(fixtureId: string): ParsedFixture | null {
  const splitFixtureId = fixtureId.split('.');

  if (splitFixtureId.length === 2) {
    const [, row, rowNum] = splitFixtureId[0].match(/([a-zA-Z]+)(\d+)/) || [];
    const [, fixtureType, columnNum] = splitFixtureId[1].match(/([a-zA-Z]+)(\d+)/) || [];

    if (row && rowNum && fixtureType && columnNum) {
      return {
        row,
        rowNumber: parseInt(rowNum, 10),
        fixtureType,
        columnNumber: parseInt(columnNum, 10),
      };
    }
  }

  return null; // Return null for invalid fixture IDs
}

/**
 * parse FixtureIds to ParsedFixture[]
 */
export function parseFixtureIds(fixtureIds: string[]): ParsedFixture[] {
  const parsedFixtureIds: ParsedFixture[] = [];

  fixtureIds.forEach((fixtureId) => {
    const parsedFixture = parseFixtureId(fixtureId);

    if (parsedFixture) {
      parsedFixtureIds.push(parsedFixture);
    }
  });

  return parsedFixtureIds;
}
/**
 * get zoneName from Macroplanogram, use concatnated subcategoryName for now
 */
export const getConcatnatedSubcategoryName = (macroplanogram: AgileMacroplanogram): string =>
  macroplanogram.storeSubcategories
    ?.sort((a: storeSubcategory, b: storeSubcategory) => a.subcategoryName.localeCompare(b.subcategoryName))
    .map((item) => item.subcategoryName)
    .join(DELIMITER) as string;

/**
 * get subcategoryName List from Macroplanogram
 */

export const getSubcategoryNames = (macroplanogram: AgileMacroplanogram): string[] =>
  macroplanogram.storeSubcategories?.map((item) => item.subcategoryName) as string[];

/**
 * get subcategoryId List from Macroplanogram
 */
export const getSubcategoryIds = (macroplanogram: AgileMacroplanogram): string[] =>
  macroplanogram.storeSubcategories?.map((item) => item.subcategoryId) as string[];

/**
 * get Macroplanogram Data including zoneId
 * For aggregate macroplanograms, expands into individual zones using storeSubcategories
 */
export const parseMacroData = (macroplanogramList: AgileMacroplanogram[]): MacroData[] =>
  macroplanogramList
    ?.filter(
      (macroplanogram) =>
        macroplanogram.storeSubcategories &&
        macroplanogram.storeSubcategories?.length > 0 &&
        macroplanogram.fixtureIds &&
        macroplanogram.fixtureIds?.length > 0,
    )
    .flatMap((macroplanogram) => {
      const { entryId, macroplanogramName, storeSubcategories } = macroplanogram;

      // Check if this is an aggregate macroplanogram with zone-specific data
      const hasZoneData = storeSubcategories?.some((sc) => sc.zoneFixtureIds && sc.zoneFixtureIds.length > 0);

      if (hasZoneData && storeSubcategories) {
        // Aggregate model: Group subcategories by shared fixtures
        const zoneGroups = new Map<string, storeSubcategory[]>();
        storeSubcategories?.forEach((sc) => {
          const fixtureKey = (sc.zoneFixtureIds || []).sort().join(',');
          if (!zoneGroups.has(fixtureKey)) {
            zoneGroups.set(fixtureKey, []);
          }
          zoneGroups.get(fixtureKey)?.push(sc);
        });

        // Create MacroData for each zone group with unique ID
        return Array.from(zoneGroups.values()).map((subcategoriesInZone) => {
          const zoneName = subcategoriesInZone.map((sc) => sc.subcategoryName).join(DELIMITER);
          const zoneFixtureIds = subcategoriesInZone[0]?.zoneFixtureIds || [];
          const fixtureIds = parseFixtureIds(zoneFixtureIds);
          const subcategoryIds = subcategoriesInZone.map((sc) => sc.subcategoryId);
          const subcategoryNames = subcategoriesInZone.map((sc) => sc.subcategoryName);

          // Use subcategory IDs as unique identifier
          // Single: just the ID, Multiple: joined with | (sorted for consistency)
          const uniqueZoneId = subcategoryIds.length === 1 ? subcategoryIds[0] : [...subcategoryIds].sort().join('|');

          return {
            zoneName,
            fixtureIds,
            zoneId: uniqueZoneId,
            unParsedFixtureIds: zoneFixtureIds,
            subcategoryIds,
            subcategoryNames,
            concatnatedSubcategoryName: zoneName,
            startTime: macroplanogram.startTime,
            capacityPercentages: subcategoriesInZone.map((sc) => sc.capacityPercentage ?? null),
            macroplanogramId: entryId,
            linkId: macroplanogram.linkId,
            categoryId: macroplanogram.categoryId,
          };
        });
      }

      // Fallback: no zone-specific fixture data — one MacroData per macroplanogram
      const concatnatedSubcategoryName = getConcatnatedSubcategoryName(macroplanogram);
      const zoneName = macroplanogramName ?? concatnatedSubcategoryName;
      const fixtureIds = parseFixtureIds(macroplanogram.fixtureIds ? macroplanogram.fixtureIds : []);
      const unParsedFixtureIds = macroplanogram.fixtureIds ? macroplanogram.fixtureIds : [];
      const subcategoryIds = getSubcategoryIds(macroplanogram);
      const subcategoryNames = getSubcategoryNames(macroplanogram);

      return [
        {
          zoneName,
          fixtureIds,
          zoneId: entryId,
          unParsedFixtureIds,
          subcategoryIds,
          subcategoryNames,
          concatnatedSubcategoryName,
          startTime: macroplanogram.startTime,
          macroplanogramId: entryId,
          linkId: macroplanogram.linkId,
          categoryId: macroplanogram.categoryId,
        },
      ];
    });

/**
 * assign random color to fixture based on uphl2 if no assigned color to this uphl2
 */
export function getZoneColor(str: string, opacity = 1): string {
  if (str.includes('/')) return '#b2babb'; // use a specific color for mixing zone
  const plusStr = str + str;
  let hash = 0;
  let i = 0;

  const color = [0, 0, 0];

  while (i < plusStr.length) {
    // eslint-disable-next-line no-bitwise
    hash = plusStr.charCodeAt(i) + ((hash << 5) - hash);
    i += 1;
  }

  i = 0;
  while (i < 3) {
    // eslint-disable-next-line no-bitwise
    const value = (hash >> (i * 8)) & 0xff;
    color[i] = value;
    i += 1;
  }

  // Check that opacity is between 0 and 1
  if (opacity < 0 || opacity > 1) {
    throw new Error('Opacity must be between 0 and 1');
  }

  // Format the result as rgba
  return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${opacity})`;
}
